"use client"

import { useHasConsent } from "@webscraping/consent"
import { usePostHog } from "posthog-js/react"
import { useEffect, useRef } from "react"
import { isDev } from "~/env"

export type ViewTrackerProperties = Record<string, boolean | number | string | string[] | null>

type ViewTrackerProps = Readonly<{
  eventName: string
  properties: ViewTrackerProperties
  trackingKey: string
  logScope: string
}>

function useCaptureViewEvent({
  eventName,
  properties,
  trackingKey,
  logScope,
}: ViewTrackerProps): void {
  const posthog = usePostHog()
  const measurementAllowed = useHasConsent("measurement")
  const trackedEvent = useRef<{ eventName: string; trackingKey: string } | null>(null)

  useEffect(() => {
    const isTracked = () =>
      trackedEvent.current?.eventName === eventName &&
      trackedEvent.current.trackingKey === trackingKey

    const markTracked = () => {
      trackedEvent.current = { eventName, trackingKey }
    }

    const capture = (): "captured" | "retry" | "skipped" => {
      if (isTracked()) return "captured"

      if (isDev) {
        markTracked()
        console.log(`Tracking event: ${eventName}`, properties)
        return "captured"
      }

      if (!measurementAllowed) return "skipped"
      if (!posthog || !posthog.__loaded || !posthog.has_opted_in_capturing()) return "retry"

      try {
        const result = posthog.capture(eventName, properties)
        if (result) {
          markTracked()
          return "captured"
        }
      } catch (error) {
        console.error(`[${logScope}] failed to capture ${eventName}`, error)
      }
      return "skipped"
    }

    if (capture() !== "retry") return

    const retry = globalThis.setInterval(() => {
      if (capture() !== "retry") {
        globalThis.clearInterval(retry)
      }
    }, 250)

    return () => globalThis.clearInterval(retry)
  }, [eventName, logScope, measurementAllowed, posthog, properties, trackingKey])
}

export function ViewTracker(props: ViewTrackerProps) {
  useCaptureViewEvent(props)

  return null
}
